import { useMemo, useState } from 'react'
import { ArrowLeft, Download, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { AsyncMultiSelect, DateRange } from '@/shared/ui/filters'
import {
  BucketAxis, ChartFrame, FunnelBars, OutcomeStrip, RankedBars, Sparkbars,
} from '@/shared/ui/charts'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { fmtNumber, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import {
  INTAKE_CHANNEL_LABEL, REGISTRATION_REASON_CODE, TRANSFER_REASON_LABEL,
  transferAnalysis,
} from './api'
import { AskPanel, CouncilBoard } from '@/shared/ui/council'
import { advise, ask, transferContext } from './analysis/council'

/**
 * Transfer analysis.
 *
 * ## Why this is a separate screen from the queue
 *
 * The queue answers "what do I do next"; this answers "how are we doing". They are read
 * by different people at different times, and mixing them makes both worse — a caseworker
 * scrolls past six charts to reach their work, and a campaign manager reads a table of
 * fifty rows looking for a number.
 *
 * ## Reading order is deliberate
 *
 * It descends from the question a campaign manager asks to the one a ward coordinator
 * asks, so nobody has to scroll to find their own row:
 *
 * 1. **Four numbers.** Recorded, success rate, median days, still open — with the
 *    change against the preceding window of equal length, because a number with nothing
 *    to compare it to is not information.
 * 2. **The funnel.** Where requests stop. This is the only chart that says *why* the
 *    top-line number is what it is.
 * 3. **Outcomes.** How the resolved ones ended.
 * 4. **Stations.** Who gained, who lost. The operational answer.
 * 5. **Trend, reasons, channels.** The texture underneath.
 *
 * ## On colour, since it was asked for
 *
 * The bars are not a rainbow, and that is a decision rather than an omission.
 *
 * Colouring a ranked bar chart by category spends the identity channel on something the
 * bar length already says — and this app's chart tokens fail a colour-vision check on
 * adjacent pairs anyway (worst ΔE 3.1 under protanopia against a 6 floor), so hues here
 * would be decoration that some readers cannot decode. Colour is used in exactly the two
 * places it carries meaning nothing else can:
 *
 * - **The funnel** is *ordinal* — the stages have an order — so it takes a one-hue ramp
 *   with monotone lightness (`--ordinal-1..5`). Nobody has to tell two hues apart, only
 *   lighter from darker.
 * - **Outcomes** are *status*, a reserved scale, and they ship with a label and a count
 *   beside every segment so the colour is reinforcement rather than the only reading.
 *
 * Everything else encodes with length and position, which is legible in both themes,
 * under any colour vision, and in greyscale print.
 */

const PRESETS: { id: string; label: string; days: number }[] = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
  { id: '365', label: '12 months', days: 365 },
]

const OUTCOME_TONES: Record<string, 'good' | 'warn' | 'bad' | 'neutral'> = {
  RECORDED: 'good',
  CONFIRMED: 'good',
  REJECTED: 'bad',
  FAILED_AT_IEBC: 'bad',
  REVERSED: 'bad',
  WITHDRAWN: 'neutral',
  EXPIRED: 'warn',
  INELIGIBLE: 'warn',
}

const STAGE_LABEL: Record<string, string> = {
  RAISED: 'Raised',
  SUBMITTED: 'Submitted for checking',
  APPROVED: 'Approved',
  LODGED: 'Lodged with the IEBC',
  RECORDED: 'Recorded on our register',
}

function isoDaysAgo(days: number) {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10)
}

export function TransferAnalysisPage() {
  const canRead = useCan('relocation:read')
  const [params, setParams] = useSearchParams()

  const preset = params.get('preset') ?? '30'
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const kind = params.get('kind') ?? ''
  const [stations, setStations] = useState<{ value: string; label: string }[]>([])
  const [rank, setRank] = useState<'gained' | 'lost' | 'net'>('gained')
  // The tab lives in the URL alongside the window and the filters, so a board somebody is
  // looking at can be sent to somebody else and mean the same thing.
  const tab = params.get('tab') === 'council' ? 'council' : 'board'

  const patch = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') sp.delete(k); else sp.set(k, v)
    }
    setParams(sp, { replace: true })
  }

  // An explicit range wins over a preset; choosing a preset clears the range. Two
  // controls that both set the window have to agree on which one is in force.
  const effectiveFrom = from || isoDaysAgo(Number(preset) || 30)
  const effectiveTo = to || new Date().toISOString().slice(0, 10)
  const stationIds = stations.map((s) => s.value)

  const data = useLoad(
    () => (canRead
      ? transferAnalysis({
          from: effectiveFrom,
          to: effectiveTo,
          kind: kind || undefined,
          stationId: stationIds.length ? stationIds : undefined,
        })
      : Promise.resolve(null)),
    [canRead, effectiveFrom, effectiveTo, kind, stationIds.join(',')])

  const findings = useMemo(() => (data.data ? advise(data.data) : []), [data.data])

  if (!canRead) return <Forbidden what="transfer analysis" />

  const d = data.data
  const rankedStations = [...(d?.byStation ?? [])]
    .sort((a, b) => (rank === 'gained' ? b.recorded - a.recorded
      : rank === 'lost' ? b.lost - a.lost
        : b.net - a.net))
    .slice(0, 12)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Transfer analysis"
        description="Every number is for the range and filters below, and for your scope. The IEBC performs transfers; these count the ones we recorded."
        right={
          <div className="flex gap-1.5">
            <button type="button" className="erp-btn" aria-label="Refresh"
                    onClick={() => void data.reload()}>
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            <Link to="/transfers" className="erp-btn" aria-label="Back to the queue">
              <ArrowLeft className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Queue</span>
            </Link>
          </div>
        }
      />

      {/* Filters in one row above the charts, never a sidebar eating the width. */}
      <Panel dense>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1" role="group" aria-label="Range">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={!from && !to && preset === p.id}
                onClick={() => patch({ preset: p.id, from: null, to: null })}
                className={cn('erp-btn',
                  !from && !to && preset === p.id && 'border-chocolate text-chocolate font-medium')}
              >
                {p.label}
              </button>
            ))}
          </div>

          <DateRange
            from={from}
            to={to}
            onChange={(next) => patch({ from: next.from, to: next.to, preset: null })}
          />

          <select
            className="erp-input"
            value={kind}
            onChange={(e) => patch({ kind: e.target.value })}
            aria-label="Kind"
          >
            <option value="">Transfers and registrations</option>
            <option value="TRANSFER">Transfers only</option>
            <option value="REGISTRATION">Registrations only</option>
          </select>

          <AsyncMultiSelect
            label="Station"
            selected={stations}
            onChange={setStations}
            placeholder="Search polling stations"
            search={async (term) => {
              const page = await listUnits({ q: term, type: ['POLLING_STATION'] } as never, 0, 10)
              return page.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
            }}
          />
        </div>
      </Panel>

      <ErrorStrip message={data.error} />

      {data.loading && !d ? (
        <Panel><LoadingBlock label="Counting…" /></Panel>
      ) : !d ? (
        <Panel>
          <EmptyState
            title="No analysis yet"
            hint="This screen needs the analysis endpoint. Until the backend serves it, the queue's own charts are the summary."
          />
        </Panel>
      ) : (
        <>
          <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
            {([
              { id: 'board', label: 'The board', hint: 'Movement, pipeline, stations, texture' },
              { id: 'council', label: 'AI Council', hint: 'The operation read as decisions' },
            ] as const).map((t) => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} title={t.hint}
                      onClick={() => patch({ tab: t.id === 'board' ? null : t.id })}
                      className={cn(
                        'px-3 py-2 text-erp-sm rounded-t-md border-b-2 -mb-px transition-colors',
                        tab === t.id
                          ? 'border-chocolate text-foreground font-medium'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50')}>
                {t.label}
                {t.id === 'council' && findings.some((f) => f.severity === 'critical') && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-erp-xs px-1.5 min-w-[18px] h-[18px] tabular-nums">
                    {findings.filter((f) => f.severity === 'critical').length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === 'council' ? (
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <CouncilBoard
                  findings={findings}
                  title="What the movement is saying"
                  description={`${findings.length} ${findings.length === 1 ? 'finding' : 'findings'} over ${d.from} to ${d.to}, ranked by the registrations each is worth.`}
                  stakeUnit="registration"
                  emptyMessage="Nothing in this window crosses a threshold. Widen the range if you are checking whether the operation has been slowing."
                />
              </div>
              <AskPanel
                storageKey={`transfers-council-history:${d.from}:${d.to}:${kind || 'all'}`}
                description="Plain language. The window's figures go with the question, so the answer is about this operation."
                placeholder="Is this moving votes where we need them?"
                scopeNote="It sees the counts, the pipeline and the per-station movement — not individual requests."
                suggestions={[
                  'Is this moving votes where we need them?',
                  'Where are we losing requests?',
                  'Will the open ones land in time?',
                  'Which stations are shedding voters?',
                  'Should we recruit more intake?',
                ]}
                onAsk={(q) => ask(q, findings)}
                context={() => transferContext(d, findings)}
              />
            </div>
          ) : (
          <>
          <KpiStrip>
            <MetricCard
              label="Recorded"
              value={fmtNumber(d.recorded)}
              accent
              sub={<Delta now={d.recorded} before={d.previous?.recorded} unit="vs previous" />}
            />
            <MetricCard
              label="Success rate"
              value={d.successRate == null ? '—' : `${Math.round(d.successRate * 100)}%`}
              sub={d.successRate == null
                ? 'nothing has resolved yet'
                : <Delta
                    now={d.successRate * 100}
                    before={d.previous?.successRate == null ? undefined : d.previous.successRate * 100}
                    unit="points"
                    digits={0}
                  />}
            />
            <MetricCard
              label="Median days to record"
              value={d.medianDaysToRecord == null ? '—' : String(d.medianDaysToRecord)}
              sub="raised to recorded"
            />
            <MetricCard label="Open now" value={fmtNumber(d.open)} sub="not yet at rest" />
          </KpiStrip>

          <div className="grid gap-3 lg:grid-cols-2">
            <Panel title="Where requests stop"
                   description="Survival through the lifecycle, in the range">
              <FunnelBars
                stages={(d.funnel ?? []).map((f) => ({
                  label: STAGE_LABEL[f.stage] ?? humanise(f.stage),
                  value: f.count,
                }))}
              />
            </Panel>

            <Panel title="How resolved requests ended"
                   description="Every one that reached a terminal state">
              <OutcomeStrip
                parts={Object.entries(d.outcomes ?? {})
                  .filter(([, n]) => n > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([code, value]) => ({
                    label: humanise(code),
                    value,
                    tone: OUTCOME_TONES[code] ?? 'neutral',
                  }))}
              />
              <div className="mt-4">
                <ChartFrame title="Raised" description="By day">
                  <Sparkbars points={(d.raisedDaily ?? []).map((p) => ({ label: p.date, value: p.count }))} height={64} />
                  <BucketAxis points={(d.raisedDaily ?? []).map((p) => ({ label: p.date.slice(5), value: p.count }))} />
                </ChartFrame>
              </div>
            </Panel>
          </div>

          <Panel
            title="Stations"
            description={rank === 'gained'
              ? 'Who gained the most registrations in this range'
              : rank === 'lost' ? 'Who gave up the most' : 'Net movement — gained minus lost'}
            right={
              <div className="flex gap-1" role="group" aria-label="Rank stations by">
                {(['gained', 'lost', 'net'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={rank === r}
                    onClick={() => setRank(r)}
                    className={cn('erp-btn', rank === r && 'border-chocolate text-chocolate font-medium')}
                  >
                    {r === 'gained' ? 'Gained' : r === 'lost' ? 'Lost' : 'Net'}
                  </button>
                ))}
              </div>
            }
          >
            {/* Length encodes magnitude and the labels carry identity. Colouring these
                bars by station would re-encode what their length already shows. */}
            <RankedBars
              points={rankedStations.map((s) => ({
                label: s.stationName,
                value: rank === 'gained' ? s.recorded : rank === 'lost' ? s.lost : Math.abs(s.net),
                hint: `+${s.recorded} gained · −${s.lost} lost · ${s.net >= 0 ? '+' : ''}${s.net} net`,
              }))}
              emptyMessage="No station recorded a movement in this range."
            />

            {/* The table view is not optional: it is how this is read by someone using a
                screen reader, and how anyone gets the exact numbers out. */}
            {rankedStations.length > 0 && (
              <details className="mt-4">
                <summary className="text-erp-xs text-muted-foreground cursor-pointer">
                  Show as a table
                </summary>
                <div className="mt-2">
                  <ErpTable head={<>
                    <Th>Station</Th>
                    <Th className="text-right">Gained</Th>
                    <Th className="text-right">Lost</Th>
                    <Th className="text-right">Net</Th>
                    <Th className="text-right axis-hide-md">Open</Th>
                  </>}>
                    {rankedStations.map((s) => (
                      <tr key={s.stationId}>
                        <Td>{s.stationName}</Td>
                        <Td className="text-right tabular-nums">{fmtNumber(s.recorded)}</Td>
                        <Td className="text-right tabular-nums">{fmtNumber(s.lost)}</Td>
                        <Td className={cn('text-right tabular-nums font-medium',
                          s.net > 0 && 'text-emerald-700 dark:text-emerald-400',
                          s.net < 0 && 'text-red-700 dark:text-red-400')}>
                          {s.net > 0 ? '+' : ''}{fmtNumber(s.net)}
                        </Td>
                        <Td className="text-right tabular-nums axis-hide-md">{fmtNumber(s.open)}</Td>
                      </tr>
                    ))}
                  </ErpTable>
                </div>
              </details>
            )}
          </Panel>

          <div className="grid gap-3 lg:grid-cols-2">
            <Panel title="Where the work sits" description="Rolled up to ward and above">
              <RankedBars
                points={[...(d.byUnit ?? [])]
                  .sort((a, b) => b.recorded - a.recorded)
                  .slice(0, 10)
                  .map((u) => ({
                    label: `${u.unitName} · ${humanise(u.level)}`,
                    value: u.recorded,
                    hint: `${u.recorded} gained · ${u.lost} lost`,
                  }))}
                emptyMessage="Nothing recorded in this range."
              />
            </Panel>

            <Panel title="Why they were raised">
              <RankedBars
                points={Object.entries(d.byReason ?? {})
                  .filter(([, n]) => n > 0)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([code, value]) => ({
                    label: code === REGISTRATION_REASON_CODE
                      ? 'Not yet registered'
                      : TRANSFER_REASON_LABEL[code] ?? humanise(code),
                    value,
                  }))}
                emptyMessage="Nothing in this range."
              />
              <div className="mt-4">
                <ChartFrame title="How they reached us">
                  <RankedBars
                    points={Object.entries(d.byChannel ?? {})
                      .filter(([, n]) => n > 0)
                      .sort((a, b) => b[1] - a[1])
                      .map(([code, value]) => ({
                        label: INTAKE_CHANNEL_LABEL[code] ?? humanise(code),
                        value,
                      }))}
                    emptyMessage="Nothing in this range."
                  />
                </ChartFrame>
              </div>
            </Panel>
          </div>

          <Note tone="info">
            <span className="inline-flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5" />
              Counts are for {d.from} to {d.to}
              {d.scopePath ? <> within <code className="font-mono text-erp-xs">{d.scopePath}</code></> : null}.
              Someone with a narrower scope sees a smaller number here, and that is correct
              rather than a discrepancy.
            </span>
          </Note>
          </>
          )}
        </>
      )}
    </div>
  )
}

/**
 * A number against the same-length window before it.
 *
 * "412 recorded" is a fact; "412, up 38%" is information. When there is nothing to
 * compare against, this renders nothing rather than inventing a baseline of zero, which
 * would print "up 100%" on the first week of every deployment.
 */
function Delta({
  now, before, unit, digits = 0,
}: { now: number; before?: number | null; unit: string; digits?: number }) {
  if (before == null) return <>no earlier window</>
  const diff = now - before
  if (Math.abs(diff) < 0.5 / 10 ** digits) return <>unchanged {unit}</>
  const up = diff > 0
  const pct = before === 0 ? null : (diff / before) * 100
  return (
    <span className={cn('inline-flex items-center gap-1',
      up ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400')}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? '+' : ''}{diff.toFixed(digits)}
      {pct != null && <> ({up ? '+' : ''}{pct.toFixed(0)}%)</>} {unit}
    </span>
  )
}
